import { Button } from "@/components/ui/button";
import { ProfileForm } from "@/components/profile/profile-form";
import { CompatibilityScore } from "@/components/profile/compatibility-score";
import { Link, useRoute } from "wouter";
import { useQuery } from "@tanstack/react-query";
import { useUser } from "@/hooks/use-user";
import { Loader2, Music2, Calendar, Users, Star } from "lucide-react";
import { MoodVisualization } from "@/components/events/mood-visualization";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";

export default function Profile() {
  const [, params] = useRoute("/profile/:id");
  const { user } = useUser();

  const { data: viewedUser, isLoading } = useQuery({
    queryKey: ["/api/users/" + (params?.id || user?.id)],
    enabled: Boolean(params?.id || user?.id),
  });

  // Fetch past events
  const { data: pastEvents } = useQuery({
    queryKey: ["/api/events/past"],
    enabled: Boolean(viewedUser?.id),
  });

  // Calculate event statistics
  const eventStats = pastEvents ? {
    total: pastEvents.length,
    genres: new Set(pastEvents.flatMap(event =>
      event.eventArtists?.flatMap(ea => ea.artist?.musicPreferences?.genres || []) || []
    )).size,
    artists: new Set(pastEvents.flatMap(event =>
      event.eventArtists?.map(ea => ea.artist?.username) || []
    )).size,
  } : null;

  const isOwnProfile = !params?.id || (user && params.id === user.id.toString());

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!viewedUser) {
    return (
      <div className="min-h-screen bg-background">
        <div className="container mx-auto px-4 py-8">
          <h1 className="text-2xl font-bold">User not found</h1>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b">
        <div className="container mx-auto px-4 py-4">
          <div className="flex justify-between items-center">
            <h1 className="text-2xl font-bold">
              {isOwnProfile ? "Your Profile" : `${viewedUser.username}'s Profile`}
            </h1>
            <Link href="/">
              <Button variant="ghost">Back to Home</Button>
            </Link>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-8">
        <div className="max-w-4xl mx-auto space-y-8">
          {isOwnProfile ? (
            <ProfileForm />
          ) : (
            <>
              {/* User Info Section */}
              <Card>
                <CardHeader>
                  <CardTitle>Profile Information</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <h3 className="font-medium">Display Name</h3>
                    <p>{viewedUser.displayName || viewedUser.username}</p>
                  </div>
                  <div>
                    <h3 className="font-medium">Bio</h3>
                    <p>{viewedUser.bio || "No bio yet"}</p>
                  </div>
                  <div>
                    <h3 className="font-medium">Location</h3>
                    <p>{viewedUser.location || "Not set"}</p>
                  </div>
                </CardContent>
              </Card>

              {/* Music Preferences Section */}
              {viewedUser.musicPreferences?.genres && (
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Music2 className="h-5 w-5" />
                      Music Taste Analysis
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    <MoodVisualization genres={viewedUser.musicPreferences.genres} />

                    <div className="space-y-4">
                      <h3 className="font-medium">Favorite Genres</h3>
                      <div className="flex flex-wrap gap-2">
                        {viewedUser.musicPreferences.genres.map((genre, i) => (
                          <Badge key={i} variant="secondary">
                            {genre}
                          </Badge>
                        ))}
                      </div>
                    </div>

                    {viewedUser.musicPreferences.artists?.length > 0 && (
                      <div className="space-y-4">
                        <h3 className="font-medium">Favorite Artists</h3>
                        <div className="flex flex-wrap gap-2">
                          {viewedUser.musicPreferences.artists.map((artist, i) => (
                            <Badge key={i}>
                              {artist}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}

              {/* Event Statistics */}
              {eventStats && (
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Star className="h-5 w-5" />
                      Event Statistics
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="text-center p-4 bg-secondary rounded-lg">
                        <Calendar className="h-8 w-8 mx-auto mb-2" />
                        <div className="text-2xl font-bold">{eventStats.total}</div>
                        <div className="text-sm text-muted-foreground">Events Attended</div>
                      </div>
                      <div className="text-center p-4 bg-secondary rounded-lg">
                        <Music2 className="h-8 w-8 mx-auto mb-2" />
                        <div className="text-2xl font-bold">{eventStats.genres}</div>
                        <div className="text-sm text-muted-foreground">Genres Explored</div>
                      </div>
                      <div className="text-center p-4 bg-secondary rounded-lg">
                        <Users className="h-8 w-8 mx-auto mb-2" />
                        <div className="text-2xl font-bold">{eventStats.artists}</div>
                        <div className="text-sm text-muted-foreground">Artists Seen</div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Compatibility Score */}
              <CompatibilityScore userId={viewedUser.id} />
            </>
          )}
        </div>
      </main>
    </div>
  );
}